<template>
  <!-- Le bandeau vit dans le conteneur des bandeaux du HAUT, à l'endroit où
       l'œil arrive : une annonce qu'on peut manquer ne sert à rien. La v1 s'y
       posait déjà, mais en `fixed; top: 0` sans que rien ne compense sa
       hauteur — elle masquait donc le début de la page. Ici le conteneur publie
       sa hauteur dans `--top-bars-height`, et tout ce qui touche le haut de
       l'écran s'en écarte : le corps de la page (padding plus bas), l'en-tête
       collant des listes, le bouton du menu mobile. Il gère aussi le décalage
       de la barre latérale, rail v2 compris. -->
  <Teleport v-if="hasTarget" defer to="#top-bars-container">
    <Transition name="info-banner-slide" @after-leave="releaseTarget">
      <div v-if="shown" class="info-banner update-banner" role="status" aria-live="polite">
        <span class="info-banner-icon" aria-hidden="true">
          <i class="fas fa-sync-alt"></i>
        </span>

        <div class="info-banner-body">
          <p class="info-banner-title">Une nouvelle version d’Alertis est disponible</p>
          <p class="info-banner-hint">
            Terminez votre saisie en cours : la mise à jour recharge la page.
          </p>
        </div>

        <div class="info-banner-actions">
          <Button
            variant="secondary"
            size="sm"
            data-testid="update-banner-later"
            @click="$emit('dismiss-update')"
          >
            Plus tard
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon="fas fa-sync-alt"
            data-testid="update-banner-reload"
            @click="$emit('reload-app')"
          >
            Mettre à jour
          </Button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/**
 * Nouvelle interface du bandeau « une nouvelle version est disponible ».
 *
 * Deux écarts assumés avec la v1, tous documentés dans le rapport de refonte :
 * - il se pose en haut comme elle, mais sans rien recouvrir (voir le
 *   commentaire du gabarit) ;
 * - il porte les couleurs de la marque (accent d'information du kit) et non un
 *   dégradé violet hors palette, et n'anime plus son icône en boucle ;
 * - la croix sans libellé devient un bouton « Plus tard » : la charte interdit
 *   l'icône cliquable seule, et l'attribut `title` comme infobulle.
 */
import { computed, nextTick, onUnmounted, ref, watch, watchEffect } from 'vue'
import Button from '../ui/Button.vue'

const props = defineProps({
  hasNewVersion: Boolean,
  isEmbedRoute: Boolean,
  isPublicRoute: Boolean
})

defineEmits(['reload-app', 'dismiss-update'])

const shown = computed(
  () => props.hasNewVersion && !props.isEmbedRoute && !props.isPublicRoute
)

// Le conteneur d'accueil appartient à AppLayout : il est monté bien avant
// qu'une nouvelle version puisse être détectée (le premier sondage ne fait
// qu'enregistrer la version courante). On vérifie tout de même sa présence
// plutôt que de laisser Teleport échouer sur une cible absente.
const hasTarget = ref(false)
watch(
  shown,
  async (visible) => {
    if (!visible) return
    await nextTick()
    hasTarget.value = Boolean(document.getElementById('top-bars-container'))
  },
  { immediate: true }
)

// Une fois le bandeau sorti, on démonte le Teleport : sa cible disparaît avec
// le gabarit sur les routes publiques et intégrées, où le bandeau ne s'affiche
// pas. Le garde sur `shown` évite de démonter un bandeau que la sortie aurait
// croisé (une version plus récente annoncée pendant l'animation).
const releaseTarget = () => {
  if (!shown.value) hasTarget.value = false
}

// Le bandeau flotte au-dessus de la page : sans cela, il masquerait le début du
// contenu (le titre d'une liste, la première section d'une fiche).
watchEffect(() => {
  document.body.classList.toggle('update-banner-open', shown.value)
})
onUnmounted(() => document.body.classList.remove('update-banner-open'))
</script>

<style scoped>
/* L'anatomie (icône, texte, actions, transition, repli mobile) vient de
   `src/styles/info-banner.css`, chargée globalement par `style.css`. Ne
   reste ici que ce qui est propre à CE bandeau : la classe qui pousse la
   page.
   Le bandeau flotte au-dessus du haut de l'écran : la page descend d'autant,
   sans quoi sa première ligne resterait cachée dessous — c'est exactement ce
   que faisait l'ancienne interface. Le padding va sur <body> (conteneur de
   défilement de toutes les pages du back-office) et non sur `.main` : le style
   scopé du gabarit y gagnerait en spécificité. La barre latérale est fixée,
   elle ne bouge donc pas. La hauteur vient de TopBarsContainer. */
:global(body.update-banner-open) {
  padding-top: var(--top-bars-height, 0px);
}
</style>
